import { from, of } from "rxjs";
import { catchError, filter, map, mergeMap } from "rxjs/operators";
import { isActionOf, type PayloadAction } from "typesafe-actions";
import { documentMetadata } from "../../state/config/config.selector";
import * as templatesActions from "../../state/templates/templates.actions";
import { organizationCategory } from "../../state/templates/templates.reducer";
import type { PublishTemplatePayload } from "../../state/templates/types";
import { userToken } from "../../state/user/user.selector";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import { waitForToken } from "../helpers/waitForState";
import type { AppEpic } from "../types";
import { QUICK_ACCESS_CATEGORY_ID } from "./types";

const updateCategories = (
  originalCategories: string[],
  newCategories: string[],
) => {
  const addedCategories = newCategories.filter(
    (id) => !originalCategories.includes(id),
  );
  const removedCategories = originalCategories.filter(
    (id) => !newCategories.includes(id),
  );

  return { addedCategories, removedCategories };
};

export const fetchTemplateCategoriesEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.fetchTemplateCategories.request)),
    waitForToken(state$),
    mergeMap(() =>
      templateRequests
        .getTemplateCategories(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
        )
        .pipe(
          mergeMap((res) => {
            // 'Organization' category is not stored in the database. It is exclusively displayed on the client side.
            // Its inclusion here is for the purpose of keeping this category in the same place in state in order to
            // simplify Redux operations like update / remove etc.
            const clientCategories = {
              ...res,
              categories: [...res.categories, organizationCategory],
            };
            return of(
              templatesActions.fetchTemplateCategories.success(
                clientCategories,
              ),
            );
          }),
          catchError((e) =>
            of(templatesActions.fetchTemplateCategories.failure(e)),
          ),
        ),
    ),
  );

export const fetchTemplatesEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.fetchTemplates.request)),
    waitForToken(state$),
    mergeMap((action) =>
      templateRequests
        .getTemplates(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          action.payload.categoryId,
        )
        .pipe(
          mergeMap((templates) =>
            of(
              templatesActions.fetchTemplates.success({
                ...action.payload,
                ...templates,
              }),
            ),
          ),
          catchError((e) => of(templatesActions.fetchTemplates.failure(e))),
        ),
    ),
  );

export const fetchOrganizationTemplatesEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.fetchOrganizationTemplates.request)),
    waitForToken(state$),
    mergeMap(() =>
      templateRequests
        .getOrganizationTemplates(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
        )
        .pipe(
          mergeMap((templates) =>
            of(templatesActions.fetchOrganizationTemplates.success(templates)),
          ),
          catchError((e) =>
            of(templatesActions.fetchOrganizationTemplates.failure(e)),
          ),
        ),
    ),
  );

export const fetchTemplateDetailsEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.fetchTemplateDetails.request)),
    waitForToken(state$),
    mergeMap((action) => {
      return templateRequests
        .getTemplateById(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          action.payload,
        )
        .pipe(
          mergeMap((template) =>
            of(templatesActions.fetchTemplateDetails.success(template)),
          ),
          catchError((e) =>
            of(templatesActions.fetchTemplateDetails.failure(e)),
          ),
        );
    }),
  );

export const deleteTemplateEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.deleteTemplate.request)),
    waitForToken(state$),
    mergeMap((action) => {
      const { templateId } = action.payload;

      return templateRequests
        .deleteTemplate(
          documentMetadata(state$.value),
          userToken(state$.value),
          templateId,
        )
        .pipe(
          map(() => templatesActions.deleteTemplate.success(action.payload)),
          catchError((e) => of(templatesActions.deleteTemplate.failure(e))),
        );
    }),
  );

export const createTemplateEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.createTemplate.request)),
    waitForToken(state$),
    mergeMap((action) =>
      templateRequests
        .createTemplate(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          action.payload,
        )
        .pipe(
          mergeMap((template) => {
            const { categoryIds, templateId } = action.payload;

            if (categoryIds && categoryIds.length > 0) {
              const publishPayload: PublishTemplatePayload = {
                categoryIds: categoryIds,
                templateToCategory: { templateId, orderRank: null },
              };

              return of(
                templatesActions.createTemplate.success(template),
                templatesActions.publishTemplate.request(publishPayload),
              );
            } else {
              return of(
                templatesActions.createTemplate.success(template),
                workspacesActions.getWorkspaceDetails.request(
                  template.workspaceId,
                ),
              );
            }
          }),
          catchError((e) => of(templatesActions.createTemplate.failure(e))),
        ),
    ),
  );

export const publishTemplateEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.publishTemplate.request)),
    waitForToken(state$),
    mergeMap((action) =>
      templateRequests
        .publishTemplate(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          action.payload.categoryIds,
          action.payload.templateToCategory,
        )
        .pipe(
          mergeMap(() => {
            const isPublishingToQuickAccess = action.payload.categoryIds.some(
              (c) => c === QUICK_ACCESS_CATEGORY_ID,
            );
            const workspaceId =
              state$.value.context.workspaces.selectedWorkspaceID;

            const requests: PayloadAction<any, any>[] = [
              templatesActions.publishTemplate.success({
                templateId: action.payload.templateToCategory.templateId,
                categoryIds: action.payload.categoryIds,
              }),
            ];

            if (workspaceId) {
              requests.push(
                workspacesActions.getWorkspaceDetails.request(workspaceId),
              );
            }

            if (isPublishingToQuickAccess) {
              requests.push(
                templatesActions.fetchTemplates.request({
                  categoryId: QUICK_ACCESS_CATEGORY_ID,
                }),
              );
            }

            return from(requests).pipe(mergeMap((r) => of(r)));
          }),
          catchError((e) => of(templatesActions.publishTemplate.failure(e))),
        ),
    ),
  );

export const removeTemplateCategoriesEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.removeTemplateCategories.request)),
    waitForToken(state$),
    mergeMap((action) =>
      templateRequests
        .removeTemplateFromCategories(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          action.payload.categoryIds,
          action.payload.templateId,
        )
        .pipe(
          mergeMap(() => {
            const isRemovingQuickAccessTemplates =
              action.payload.categoryIds.some(
                (c) => c === QUICK_ACCESS_CATEGORY_ID,
              );

            if (isRemovingQuickAccessTemplates) {
              return of(
                templatesActions.removeTemplateCategories.success({
                  templateId: action.payload.templateId,
                  categoryIds: action.payload.categoryIds,
                }),
                templatesActions.fetchTemplates.request({
                  categoryId: QUICK_ACCESS_CATEGORY_ID,
                }),
              );
            }

            return of(
              templatesActions.removeTemplateCategories.success({
                templateId: action.payload.templateId,
                categoryIds: action.payload.categoryIds,
              }),
            );
          }),
          catchError((e) =>
            of(templatesActions.removeTemplateCategories.failure(e)),
          ),
        ),
    ),
  );

export const updateTemplateEpic: AppEpic = (
  action$,
  state$,
  { templateRequests },
) =>
  action$.pipe(
    filter(isActionOf(templatesActions.updateTemplateDetails.request)),
    waitForToken(state$),
    mergeMap((action) => {
      const { templateId, details, originalTemplateIds, updatedTemplatesIds } =
        action.payload;

      return templateRequests
        .updateTemplate(
          state$.value.context.config.serviceConfig.documentMetadata,
          state$.value.context.user.token,
          templateId,
          details,
        )
        .pipe(
          mergeMap((res) => {
            const { addedCategories, removedCategories } = updateCategories(
              originalTemplateIds,
              updatedTemplatesIds,
            );
            const templateToCategory = { templateId, orderRank: null };

            return of(
              templatesActions.publishTemplate.request({
                categoryIds: addedCategories,
                templateToCategory,
              }),
              templatesActions.removeTemplateCategories.request({
                categoryIds: removedCategories,
                templateId,
              }),
              templatesActions.updateTemplateDetails.success(res),
            );
          }),
          catchError((e) =>
            of(templatesActions.updateTemplateDetails.failure(e)),
          ),
        );
    }),
  );
